import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, Save, Trash2, ImagePlus, Star, Loader2, AlertCircle, X, Upload, Plus, Image as ImageIcon, Search,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import { Toaster, toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const garmentKey = value => (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').replace('duffle_bag', 'duffel_bag');

// Product kinds drive which sections of the form are visible.
const KIND_OPTIONS = [
  {
    value: 'school_uniform',
    label: 'School Uniform',
    desc: 'Tied to a partner school. Customer just picks size/color.',
  },
  {
    value: 'school_uniform_bundle',
    label: 'School Uniform Bundle',
    desc: 'Multi-shirt package with optional outerwear and upgrades.',
  },
  {
    value: 'custom_apparel',
    label: 'Custom Apparel',
    desc: 'Embroidery / printing with placement library defaults.',
  },
  {
    value: 'ready_made_merch',
    label: 'Ready-Made Merch',
    desc: 'Stocked item — no decoration or school configuration.',
  },
];

const FIT_OPTIONS = [
  { value: 'youth', label: 'Youth' },
  { value: 'adult', label: 'Adult' },
  { value: 'ladies', label: 'Ladies' },
];

// Sizes per fit — kept in the form for now so admins can preview which sizes
// the customer-facing flow will offer. Phase 2 will pull this from product_type_sizes.
const SIZES_BY_FIT = {
  youth: ['XS', 'S', 'M', 'L', 'XL'],
  adult: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'],
  ladies: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
};

const EMPTY = {
  name: '',
  description: '',
  short_description: '',
  base_price: 0,
  sale_price: '',
  sku: '',
  stock_quantity: 0,
  is_active: true,
  is_featured: false,
  requires_customization: false,
  allows_upload: true,
  requires_shipping: true,
  weight: '',
  category_ids: [],
  // Phase 1 additions
  product_kind: 'ready_made_merch',
  school_id: '',
  garment_type: '',
  fits: [],
  sizes: [],
  colors: [],
  bundle_config: { items: [], upgrades: [] },
  placement_overrides: null,
};

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(d);
};

const AdminProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { token } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schools, setSchools] = useState([]);
  const [placementLibrary, setPlacementLibrary] = useState([]);
  const [otherProducts, setOtherProducts] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'variants'
  const [variants, setVariants] = useState([]);
  const [variantsLoaded, setVariantsLoaded] = useState(false);
  const [savedProduct, setSavedProduct] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [cats, sc, placements, prods] = await Promise.all([
        axios.get(`${API}/api/categories/all`, { headers }),
        axios.get(`${API}/api/categories/schools`),
        axios.get(`${API}/api/admin/placements`, { headers }).catch(() => ({ data: { placements: [] } })),
        axios.get(`${API}/api/products?limit=500`, { headers }),
      ]);
      setCategories(cats.data || []);
      setSchools(sc.data || []);
      setPlacementLibrary(placements.data?.placements || []);
      setOtherProducts((prods.data || []).filter((p) => p.id !== id));

      if (isEdit) {
        const prod = await axios.get(`${API}/api/products/admin/${id}`, { headers });
        const p = prod.data;
        setSavedProduct(p);
        setForm({
          name: p.name || '',
          description: p.description || '',
          short_description: p.short_description || '',
          base_price: p.base_price ?? 0,
          sale_price: p.sale_price ?? '',
          sku: p.sku || '',
          stock_quantity: p.stock_quantity ?? 0,
          is_active: !!p.is_active,
          is_featured: !!p.is_featured,
          requires_customization: !!p.requires_customization,
          allows_upload: !!p.allows_upload,
          requires_shipping: !!p.requires_shipping,
          weight: p.weight ?? '',
          category_ids: (p.categories || []).map((c) => c.id),
          product_kind: p.product_kind || 'ready_made_merch',
          school_id: p.school_id || '',
          garment_type: (placements.data?.placements || []).find(pl => pl.product_type === garmentKey(p.garment_type))?.product_type || p.garment_type || '',
          fits: p.fits || [],
          sizes: p.sizes || [],
          colors: p.colors || [],
          bundle_config: p.bundle_config && Object.keys(p.bundle_config).length
            ? { items: [], upgrades: [], ...p.bundle_config }
            : { items: [], upgrades: [] },
          placement_overrides: p.placement_overrides || null,
        });
        setImages(p.images || []);
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, headers]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const toNumOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr('');
    const kind = form.product_kind;
    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      short_description: form.short_description || null,
      base_price: Number(form.base_price) || 0,
      sale_price: toNumOrNull(form.sale_price),
      sku: form.sku || null,
      stock_quantity: parseInt(form.stock_quantity, 10) || 0,
      is_active: !!form.is_active,
      is_featured: !!form.is_featured,
      requires_customization: kind === 'custom_apparel' || (kind === 'school_uniform' && !!form.requires_customization),
      allows_upload: !!form.allows_upload,
      requires_shipping: !!form.requires_shipping,
      weight: toNumOrNull(form.weight),
      category_ids: form.category_ids,
      product_kind: kind,
      // Only send fields relevant to the chosen kind; otherwise null them out.
      school_id: (kind === 'school_uniform' || kind === 'school_uniform_bundle') ? (form.school_id || null) : null,
      garment_type: (kind === 'custom_apparel' || kind === 'school_uniform') ? (form.garment_type || null) : null,
      fits: form.fits,
      sizes: form.sizes || [],
      colors: form.colors || [],
      bundle_config: kind === 'school_uniform_bundle' ? form.bundle_config : {},
      placement_overrides: ['custom_apparel','school_uniform'].includes(kind) ? form.placement_overrides : null,
    };
    try {
      let savedProduct;
      if (isEdit) {
        const r = await axios.put(`${API}/api/products/${id}`, payload, { headers });
        savedProduct = r.data;
        toast.success('Product saved');
      } else {
        const r = await axios.post(`${API}/api/products`, payload, { headers });
        savedProduct = r.data;
        toast.success('Product created');
        navigate(`/admin/products/${savedProduct.id}/edit`, { replace: true });
        return;
      }
      setSavedProduct(savedProduct);
      if (savedProduct?.images) setImages(savedProduct.images);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/products/${id}`, { headers });
      toast.success('Product deleted');
      navigate('/admin/products');
    } catch (e) {
      setErr(formatErr(e));
    }
  };

  const toggleCategory = (catId) => {
    setForm((f) => ({
      ...f,
      category_ids: f.category_ids.includes(catId)
        ? f.category_ids.filter((x) => x !== catId)
        : [...f.category_ids, catId],
    }));
  };

  const toggleFit = (fit) => {
    setForm((f) => ({
      ...f,
      fits: f.fits.includes(fit) ? f.fits.filter((x) => x !== fit) : [...f.fits, fit],
    }));
  };

  const addBundleItem = (productId) => {
    if (!productId) return;
    setForm((f) => {
      const items = f.bundle_config.items || [];
      if (items.some((it) => it.product_id === productId)) return f;
      return {
        ...f,
        bundle_config: {
          ...f.bundle_config,
          items: [...items, { product_id: productId, quantity: 1 }],
        },
      };
    });
  };

  const updateBundleItemQty = (productId, quantity) => {
    setForm((f) => ({
      ...f,
      bundle_config: {
        ...f.bundle_config,
        items: (f.bundle_config.items || []).map((it) =>
          it.product_id === productId ? { ...it, quantity: Math.max(1, parseInt(quantity, 10) || 1) } : it
        ),
      },
    }));
  };

  const removeBundleItem = (productId) => {
    setForm((f) => ({
      ...f,
      bundle_config: {
        ...f.bundle_config,
        items: (f.bundle_config.items || []).filter((it) => it.product_id !== productId),
      },
    }));
  };

  const addUpgrade = () => {
    setForm((f) => ({
      ...f,
      bundle_config: {
        ...f.bundle_config,
        upgrades: [...(f.bundle_config.upgrades || []), { label: '', price: 0, requires_text_input: false }],
      },
    }));
  };

  const updateUpgrade = (idx, patch) => {
    setForm((f) => ({
      ...f,
      bundle_config: {
        ...f.bundle_config,
        upgrades: (f.bundle_config.upgrades || []).map((u, i) => (i === idx ? { ...u, ...patch } : u)),
      },
    }));
  };

  const removeUpgrade = (idx) => {
    setForm((f) => ({
      ...f,
      bundle_config: {
        ...f.bundle_config,
        upgrades: (f.bundle_config.upgrades || []).filter((_, i) => i !== idx),
      },
    }));
  };

  // ── Image management (unchanged from before) ──
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/api/uploads/admin`, fd, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });
    return res.data.public_url || res.data.url;
  };

  const addImage = async (url) => {
    if (!url || !id) return;
    try {
      const r = await axios.post(
        `${API}/api/products/${id}/images`,
        { url, is_primary: images.length === 0, sort_order: images.length },
        { headers }
      );
      setImages((prev) => [...prev, r.data]);
      toast.success('Image added');
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const onUploadInput = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!id) {
      toast.error('Save the product first before uploading images.');
      return;
    }
    try {
      const url = await uploadFile(file);
      await addImage(url);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      e.target.value = '';
    }
  };

  const onPasteUrl = async () => {
    const url = window.prompt('Paste the image URL:');
    if (url) await addImage(url.trim());
  };

  const removeImage = async (imageId) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      await axios.delete(`${API}/api/products/${id}/images/${imageId}`, { headers });
      setImages((prev) => prev.filter((i) => i.id !== imageId));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setPrimary = async (imageId) => {
    try {
      await axios.put(`${API}/api/products/${id}/images/${imageId}/primary`, {}, { headers });
      setImages((prev) => prev.map((i) => ({ ...i, is_primary: i.id === imageId })));
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ── Variants management ──
  const loadVariants = useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/api/products/${id}/variants`, { headers });
      setVariants(r.data || []);
      setVariantsLoaded(true);
    } catch (e) { toast.error(formatErr(e)); }
  }, [id, headers]);

  useEffect(() => {
    if (activeTab === 'variants' && isEdit && !variantsLoaded) loadVariants();
  }, [activeTab, isEdit, variantsLoaded, loadVariants]);

  const addVariant = async () => {
    const fit = (form.fits[0] || 'adult');
    try {
      const r = await axios.post(`${API}/api/products/${id}/variants`, {
        name: `${fit.charAt(0).toUpperCase() + fit.slice(1)} / M / —`,
        options: { fit, size: 'M', color: '' },
        price_adjustment: 0,
        stock_quantity: 0,
        is_active: true,
      }, { headers });
      setVariants((prev) => [...prev, r.data]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const updateVariantRow = (variantId, patch) => {
    setVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, ...patch, options: { ...v.options, ...(patch.options || {}) } } : v)));
  };

  const saveVariantRow = async (v) => {
    try {
      await axios.put(`${API}/api/products/${id}/variants/${v.id}`, {
        name: v.name,
        sku: v.sku,
        price_adjustment: v.price_adjustment,
        stock_quantity: v.stock_quantity,
        options: v.options,
        is_active: v.is_active,
      }, { headers });
      toast.success('Variant saved');
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeVariant = async (variantId) => {
    if (!window.confirm('Remove this variant?')) return;
    try {
      await axios.delete(`${API}/api/products/${id}/variants/${variantId}`, { headers });
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading product...
        </div>
      </AdminLayout>
    );
  }

  const kind = form.product_kind;
  const isSchoolUniform = kind === 'school_uniform' || kind === 'school_uniform_bundle';
  const isBundle = kind === 'school_uniform_bundle';
  const isCustomApparel = kind === 'custom_apparel';
  const isMerch = kind === 'ready_made_merch';

  const selectedSchool = schools.find((s) => s.id === form.school_id);
  const showThreadColor = isSchoolUniform && selectedSchool?.allows_thread_color;

  const activePlacement = placementLibrary.find((pl) => pl.product_type === form.garment_type);

  // Show sizes that match the selected fits.
  const visibleSizes = form.fits.length
    ? Array.from(new Set(form.fits.flatMap((f) => SIZES_BY_FIT[f] || [])))
    : [];

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <form onSubmit={submit} className="w-full min-w-0" data-testid="admin-product-form">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <Link to="/admin/products" className="inline-flex items-center text-sm text-gray-500 hover:text-[#1B3A5C] mb-1">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to products
            </Link>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">{isEdit ? 'Edit Product' : 'Add Product'}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {savedProduct?.is_active && <><Link to={`/product/${savedProduct.slug}`} target="_blank" className="inline-flex items-center px-4 py-2 min-h-[44px] border rounded-lg text-sm">View Product</Link><button type="button" className="px-4 py-2 min-h-[44px] border rounded-lg text-sm" onClick={()=>navigator.clipboard.writeText(`${window.location.origin}/product/${savedProduct.slug}`).then(()=>toast.success('Public product link copied')).catch(()=>toast.error('Open View Product and copy its address.'))}>Copy product link</button></>}
            {isEdit && <Link to={`/admin/products/${id}/preview`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg border border-[#4A90A4] text-[#1B3A5C] text-sm font-medium" data-testid="preview-product-btn"><ExternalLink className="w-4 h-4" />Preview saved product</Link>}
            {isEdit && (
              <button type="button" onClick={remove}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium"
                data-testid="delete-product-btn">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] hover:bg-[#D88F1B] text-sm font-bold disabled:opacity-50"
              data-testid="save-product-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Product'}
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="product-form-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{err}</span>
            <button type="button" onClick={() => setErr('')} className="ml-auto text-red-500"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* TABS */}
        {isEdit && (
          <div className="flex items-center gap-1 border-b border-gray-200 mb-6" data-testid="prod-tabs">
            <button type="button" onClick={() => setActiveTab('details')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'details' ? 'border-[#F5A623] text-[#1B3A5C]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid="prod-tab-details">
              Details
            </button>
            <button type="button" onClick={() => setActiveTab('variants')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'variants' ? 'border-[#F5A623] text-[#1B3A5C]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid="prod-tab-variants">
              Variants
              {variants.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-bold">{variants.length}</span>
              )}
            </button>
          </div>
        )}

        {activeTab === 'variants' && isEdit ? (
          <VariantsPanel
            variants={variants}
            fits={form.fits}
            onAdd={addVariant}
            onUpdate={updateVariantRow}
            onSave={saveVariantRow}
            onDelete={removeVariant}
          />
        ) : (
        <>
        {/* PRODUCT KIND PICKER — drives all conditional sections below */}
        <div className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-3 mb-6" data-testid="prod-kind-card">
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">Product Type</h2>
          <p className="text-xs text-gray-500">Pick the kind of product this is. The fields below adapt automatically.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {KIND_OPTIONS.map((opt) => {
              const active = form.product_kind === opt.value;
              return (
                <label key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${active ? 'bg-[#FFF6E5] border-[#F5A623]' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                  data-testid={`prod-kind-${opt.value}`}>
                  <input type="radio" name="product_kind" value={opt.value} checked={active}
                    onChange={() => setForm({ ...form, product_kind: opt.value })}
                    className="mt-1 accent-[#F5A623]" />
                  <span>
                    <span className="block text-sm font-semibold text-[#1B3A5C]">{opt.label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{opt.desc}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Main fields */}
          <div className="lg:col-span-2 min-w-0 space-y-6">
            <Card title="Basics">
              <Field label="Product name" required>
                <input type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} data-testid="prod-name" />
              </Field>
              <Field label="Short description" hint="Shown on product cards (1-2 sentences).">
                <input type="text" value={form.short_description}
                  onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                  className={inputCls} data-testid="prod-short" />
              </Field>
              <Field label="Description" hint="Markdown allowed. Shown on the product detail page.">
                <textarea rows={4} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={`${inputCls} font-mono`} data-testid="prod-desc" />
              </Field>
            </Card>

            {/* SCHOOL — only for School Uniform / Bundle */}
            {isSchoolUniform && (
              <Card title="School" testid="prod-school-card">
                <Field label="Partner school" hint="Pick which school's logo will be embroidered on this uniform.">
                  <select value={form.school_id}
                    onChange={(e) => setForm({ ...form, school_id: e.target.value })}
                    className={inputCls} data-testid="prod-school-select">
                    <option value="">— Select a school —</option>
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.school_name || s.name}</option>
                    ))}
                  </select>
                </Field>
                {showThreadColor && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="prod-thread-color-banner">
                    <strong>Thread color picker enabled.</strong> {selectedSchool?.name} allows customers to choose the embroidery thread color. The Thread Color selector will appear on the storefront for this product.
                  </div>
                )}
              </Card>
            )}

            {/* GARMENT TYPE + DECORATION — Custom Apparel + School Uniform */}
            {(isCustomApparel || (isSchoolUniform && !isBundle)) && (
              <Card title="Decoration" testid="prod-decoration-card">
                <Field label="Garment type" hint="Pulls allowed decoration methods + placement defaults from the placement library.">
                  <select value={form.garment_type}
                    onChange={(e) => setForm({ ...form, garment_type: e.target.value })}
                    className={inputCls} data-testid="prod-garment-type-select">
                    <option value="">— Select a garment type —</option>
                    {placementLibrary.map((pl) => (
                      <option key={pl.id} value={pl.product_type}>{pl.label}</option>
                    ))}
                  </select>
                </Field>
                {isCustomApparel && <div className="space-y-3">
                  <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={form.placement_overrides?.garment_source === 'byo'} onChange={e => setForm({ ...form, placement_overrides: { ...(form.placement_overrides || {}), garment_source: e.target.checked ? 'byo' : 'shop' } })} />Customer supplies the garment (decoration service only)</label>
                  {form.placement_overrides?.garment_source === 'byo' && <div className="space-y-2">
                    <p className="text-sm text-gray-600">Enter the customer-supplied price per placement. Unpriced choices cannot be purchased. Garment cost is excluded.</p>
                    {(activePlacement?.locations || []).flatMap(loc => (loc.methods || []).flatMap(method => (method === 'printing' ? ['printing'] : loc.complexities || []).map(complexity => {
                      const key = `${method}:${loc.id}:${complexity}`;
                      return <label key={key} className="block text-sm">{loc.label} · {method} · {complexity.replace(/_/g, ' ')}
                        <input type="number" min="0.01" step="0.01" className={inputCls} value={form.placement_overrides?.byo_rates?.[key] ?? ''} onChange={e => {
                          const rates = { ...(form.placement_overrides?.byo_rates || {}) };
                          if (e.target.value === '') delete rates[key]; else rates[key] = Number(e.target.value);
                          setForm({ ...form, placement_overrides: { ...form.placement_overrides, byo_rates: rates } });
                        }} />
                      </label>;
                    })))}</div>}
                  <Field label="Setup fee per configured item" hint="Charged once per cart line, not once per garment.">
                    <input type="number" min="0" step="0.01" className={inputCls} value={form.placement_overrides?.setup_fee ?? 0} onChange={e => setForm({ ...form, placement_overrides: { ...(form.placement_overrides || {}), setup_fee: Number(e.target.value) } })} />
                  </Field>
                  <Field label="Custom design fee" hint="Leave blank to require a quote for new design work.">
                    <input type="number" min="0" step="0.01" className={inputCls} value={form.placement_overrides?.custom_design_fee ?? ''} onChange={e => setForm({ ...form, placement_overrides: { ...(form.placement_overrides || {}), custom_design_fee: e.target.value === '' ? null : Number(e.target.value) } })} />
                  </Field>
                </div>}
                {activePlacement ? (
                  <PlacementOverrideEditor
                    library={activePlacement}
                    value={form.placement_overrides}
                    onChange={(po) => setForm({ ...form, placement_overrides: po })}
                  />
                ) : form.garment_type ? (
                  <div className="text-xs text-gray-400 italic">No placement library entry found for &ldquo;{form.garment_type}&rdquo;.</div>
                ) : null}
              </Card>
            )}

            {/* FITS + SIZES + COLORS — School Uniform, Bundle, Custom Apparel, Merch */}
            {!isMerch ? null : null}
            {(isSchoolUniform || isCustomApparel || isMerch) && (
              <Card title="Fits, Sizes & Colors" testid="prod-fits-card">
                <Field label="Available fits" hint="Pick which body fits this product comes in. Sizes update below.">
                  <div className="flex flex-wrap gap-2">
                    {FIT_OPTIONS.map((opt) => {
                      const active = form.fits.includes(opt.value);
                      return (
                        <button type="button" key={opt.value} onClick={() => toggleFit(opt.value)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-[#F5A623] text-[#1B3A5C] border-[#F5A623]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                          data-testid={`prod-fit-${opt.value}`}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <Field label="Available sizes" hint="Override the default size list pulled from the selected fits. Leave empty to use defaults.">
                  <TagInput
                    values={form.sizes}
                    onChange={(next) => setForm({ ...form, sizes: next })}
                    placeholder={visibleSizes.length ? `Defaults: ${visibleSizes.join(', ')}` : 'e.g. S, M, L, XL'}
                    suggestions={visibleSizes}
                    testid="prod-sizes-input"
                    uppercase
                  />
                </Field>

                <Field label="Available colors" hint="Colors the customer can pick from on the product page. Variants on the Variants tab remain the source of truth for stock & pricing.">
                  <TagInput
                    values={form.colors}
                    onChange={(next) => setForm({ ...form, colors: next })}
                    placeholder="e.g. Black, Navy, Heather Gray"
                    testid="prod-colors-input"
                  />
                </Field>
              </Card>
            )}

            {/* BUNDLE CONFIG — Bundle only */}
            {isBundle && (
              <Card title="Bundle Contents" testid="prod-bundle-card">
                <Field label="Included products" hint="Pick each product that ships in this bundle and set how many of each. Customers don't choose — they get exactly what's here.">
                  <BundleItemsEditor
                    items={form.bundle_config.items || []}
                    catalog={otherProducts}
                    schoolId={form.school_id}
                    onAdd={addBundleItem}
                    onQty={updateBundleItemQty}
                    onRemove={removeBundleItem}
                  />
                </Field>

                <Field label="Optional upgrades" hint="Add-ons the customer can pick at order time, each with its own price.">
                  <div className="space-y-2">
                    {(form.bundle_config.upgrades || []).map((u, i) => (
                      <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center" data-testid={`prod-upgrade-row-${i}`}>
                        <input type="text" placeholder="Upgrade label (e.g. Add Name to Outerwear)"
                          value={u.label}
                          onChange={(e) => updateUpgrade(i, { label: e.target.value })}
                          className={`${inputCls} text-xs py-1.5`} data-testid={`prod-upgrade-label-${i}`} />
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">$</span>
                          <input type="number" min="0" step="0.01" placeholder="0.00"
                            value={u.price}
                            onChange={(e) => updateUpgrade(i, { price: Number(e.target.value) || 0 })}
                            className={`${inputCls} text-xs py-1.5 w-24`} data-testid={`prod-upgrade-price-${i}`} />
                        </div>
                        <label className="flex items-center gap-1 text-[11px] text-gray-600 whitespace-nowrap">
                          <input type="checkbox" checked={!!u.requires_text_input}
                            onChange={(e) => updateUpgrade(i, { requires_text_input: e.target.checked })}
                            className="accent-[#F5A623]" data-testid={`prod-upgrade-text-${i}`} />
                          Needs text input
                        </label>
                        <button type="button" onClick={() => removeUpgrade(i)}
                          className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`prod-upgrade-remove-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={addUpgrade}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-xs font-medium text-gray-600 hover:border-[#F5A623] hover:text-[#F5A623]"
                      data-testid="prod-upgrade-add">
                      <Plus className="w-3.5 h-3.5" /> Add upgrade
                    </button>
                  </div>
                </Field>
              </Card>
            )}

            {/* PRICING — always visible but stock+weight only for merch */}
            <Card title="Pricing">
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Base price ($)" required>
                  <input type="number" min="0" step="0.01" required value={form.base_price}
                    onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                    className={inputCls} data-testid="prod-base-price" />
                </Field>
                <Field label="Sale price ($)" hint="Optional. Leave blank for no sale.">
                  <input type="number" min="0" step="0.01" value={form.sale_price}
                    onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                    className={inputCls} data-testid="prod-sale-price" />
                </Field>
                <Field label="SKU">
                  <input type="text" value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    className={inputCls} data-testid="prod-sku" />
                </Field>
              </div>
              {isMerch && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Field label="Stock quantity">
                    <input type="number" min="0" step="1" value={form.stock_quantity}
                      onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })}
                      className={inputCls} data-testid="prod-stock" />
                  </Field>
                  <Field label="Weight (oz)" hint="Used by auto-label shipping if enabled.">
                    <input type="number" min="0" step="0.1" value={form.weight}
                      onChange={(e) => setForm({ ...form, weight: e.target.value })}
                      className={inputCls} data-testid="prod-weight" />
                  </Field>
                </div>
              )}
            </Card>

            {/* CATEGORIES — kept for storefront filtering, demoted in priority for school products */}
            <Card title="Categories (storefront filtering)">
              {categories.length === 0 ? (
                <p className="text-sm text-gray-400">No categories yet. <Link to="/admin/categories" className="text-[#F5A623] font-medium">Create one</Link>.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => {
                    const active = form.category_ids.includes(c.id);
                    return (
                      <button type="button" key={c.id} onClick={() => toggleCategory(c.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-[#F5A623] text-[#1B3A5C] border-[#F5A623]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                        data-testid={`prod-cat-${c.slug || c.id}`}>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* RIGHT: Visibility + Fulfillment + Images */}
          <div className="space-y-6">
            {(form.school_id || form.product_kind==='school_uniform') && <Card title="Name personalization"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requires_customization} onChange={e=>setForm(f=>({...f,requires_customization:e.target.checked}))}/>Allow names on eligible school outerwear</label><label className="block text-sm mt-3">Name charge per garment ($)<input type="number" min="0" max="10000" step="0.01" className="w-full border rounded-lg px-3 py-2 mt-1" value={form.placement_overrides?.name_personalization_fee ?? 20} onChange={e=>setForm(f=>({...f,placement_overrides:{...(f.placement_overrides||{}),name_personalization_fee:e.target.value===''?'':Number(e.target.value)}}))}/></label><p className="text-xs text-gray-500 mt-2">Only individual school outerwear is eligible. Blank names add no charge. Set 0 for complimentary names. Save before previewing.</p></Card>}
            <Card title="Visibility">
              <p className="text-sm text-gray-600 mb-3">Active products appear in <Link to="/shop" target="_blank" className="underline">Shop</Link>. Choose categories to help customers find them. A school category is not required.</p>
              {categories.filter(c=>form.category_ids.includes(c.id)).map(c=><Link key={c.id} to={`/shop?category=${c.slug}`} target="_blank" className="block text-sm underline mb-2">View {c.name} shop</Link>)}
              {isEdit && <p className="text-xs text-gray-500 mb-3">Save changes before previewing. Preview also works while the product is hidden.</p>}
              {form.is_active && !form.category_ids.length && <p role="status" className="text-sm text-amber-800 mb-3">This product appears in All Products only. Select a category to include it in a collection.</p>}
              <Toggle label="Active (shown on storefront)" checked={form.is_active} onChange={(v) => setForm({ ...form, is_active: v })} testid="prod-active" />
              <Toggle label="Featured (homepage spotlight)" checked={form.is_featured} onChange={(v) => setForm({ ...form, is_featured: v })} testid="prod-featured" />
            </Card>
            <Card title="Fulfillment">
              <Toggle label="Allows file upload at checkout" checked={form.allows_upload} onChange={(v) => setForm({ ...form, allows_upload: v })} testid="prod-allows-upload" />
              <Toggle label="Requires shipping (vs. digital/pickup-only)" checked={form.requires_shipping} onChange={(v) => setForm({ ...form, requires_shipping: v })} testid="prod-shipping" />
              {(isSchoolUniform || isCustomApparel || isBundle) && (
                <div className="text-[11px] text-gray-500 italic pt-1">
                  Customization is enabled automatically for {isBundle ? 'bundles' : isSchoolUniform ? 'school uniforms' : 'custom apparel'}.
                </div>
              )}
            </Card>

            <div className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="prod-images-card">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">Images</h2>
                <div className="flex flex-wrap gap-1">
                  <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer text-xs font-medium text-gray-700" data-testid="prod-upload-btn">
                    <Upload className="w-3.5 h-3.5" /> Upload
                    <input type="file" accept="image/*" onChange={onUploadInput} className="hidden" />
                  </label>
                  <button type="button" onClick={() => setLibraryOpen(true)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-medium text-gray-700"
                    data-testid="prod-library-btn">
                    <ImageIcon className="w-3.5 h-3.5" /> Library
                  </button>
                  <button type="button" onClick={onPasteUrl}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-medium text-gray-700"
                    data-testid="prod-url-btn">
                    <ImagePlus className="w-3.5 h-3.5" /> URL
                  </button>
                </div>
              </div>
              {!isEdit ? (
                <p className="text-xs text-gray-400">Save the product first to add images.</p>
              ) : images.length === 0 ? (
                <p className="text-xs text-gray-400">No images yet. Upload a file or paste a URL.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {images.map((img) => (
                    <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group bg-gray-50" data-testid={`prod-image-${img.id}`}>
                      <img src={img.url} alt={img.alt_text || 'Product'} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-between p-1 opacity-0 group-hover:opacity-100">
                        <button type="button" onClick={() => setPrimary(img.id)} title="Make primary"
                          className={`p-1 rounded text-xs ${img.is_primary ? 'bg-[#F5A623] text-[#1B3A5C]' : 'bg-white/90 text-gray-700 hover:bg-white'}`}>
                          <Star className={`w-3 h-3 ${img.is_primary ? 'fill-[#1B3A5C]' : ''}`} />
                        </button>
                        <button type="button" onClick={() => removeImage(img.id)} title="Remove"
                          className="p-1 rounded bg-white/90 text-red-600 hover:bg-white">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {img.is_primary && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#F5A623] text-[#1B3A5C] text-[10px] font-bold">PRIMARY</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </form>
      {libraryOpen && (
        <ImageLibraryModal
          headers={headers}
          alreadyUsed={new Set(images.map((i) => i.url))}
          onPick={async (url) => { setLibraryOpen(false); await addImage(url); }}
          onClose={() => setLibraryOpen(false)}
        />
      )}
    </AdminLayout>
  );
};

const inputCls = "w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 text-sm";

const Card = ({ title, children, testid }) => (
  <div className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-4" data-testid={testid}>
    <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">{title}</h2>
    {children}
  </div>
);

const Field = ({ label, hint, required, children }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between cursor-pointer gap-3 py-1">
    <span className="text-sm text-gray-700">{label}</span>
    <span className="relative inline-flex">
      <input type="checkbox" className="sr-only peer" checked={checked}
        onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#F5A623] transition-colors"></span>
      <span className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform shadow"></span>
    </span>
  </label>
);

const TagInput = ({ values, onChange, placeholder, suggestions, testid, uppercase }) => {
  const [draft, setDraft] = useState('');
  const normalize = (raw) => {
    const s = (raw || '').trim();
    if (!s) return '';
    return uppercase ? s.toUpperCase() : s;
  };
  const add = (raw) => {
    const next = normalize(raw);
    if (!next) return;
    if (values.includes(next)) { setDraft(''); return; }
    onChange([...values, next]);
    setDraft('');
  };
  const remove = (v) => onChange(values.filter((x) => x !== v));
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && values.length) {
      onChange(values.slice(0, -1));
    }
  };
  const handleBlur = () => { if (draft) add(draft); };

  const availableSuggestions = (suggestions || []).filter((s) => !values.includes(uppercase ? s.toUpperCase() : s));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-[#4A90A4] focus-within:border-transparent bg-white min-h-[44px]">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F5A623]/15 text-[#8B5A0F] text-xs font-semibold rounded-full" data-testid={`${testid}-tag-${v}`}>
            {v}
            <button type="button" onClick={() => remove(v)} className="text-[#8B5A0F]/60 hover:text-[#8B5A0F]" aria-label={`Remove ${v}`}>×</button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={handleBlur}
          placeholder={values.length ? '' : placeholder}
          className="flex-1 min-w-[140px] text-sm outline-none bg-transparent text-gray-900 py-1"
          data-testid={`${testid}-field`}
        />
      </div>
      {availableSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Suggested:</span>
          {availableSuggestions.map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
              className="px-2 py-0.5 rounded-full border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              data-testid={`${testid}-suggest-${s}`}>
              + {s}
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 text-[11px] text-gray-400">Press Enter or comma to add.</p>
    </div>
  );
};



const BundleItemsEditor = ({ items, catalog, schoolId, onAdd, onQty, onRemove }) => {
  const [picker, setPicker] = useState('');
  // When this bundle is tied to a school, default the dropdown to other school-uniform products
  // for that school. Admins can still toggle to "Show all" if they want to bundle non-school items.
  const [showAll, setShowAll] = useState(false);

  const candidates = (catalog || []).filter((p) => {
    if (items.some((it) => it.product_id === p.id)) return false; // already added
    if (showAll || !schoolId) return true;
    // Match on school_id (cleanest) — fallback to nothing if school_id missing on the row.
    return p.school_id === schoolId;
  });

  const byId = (id) => (catalog || []).find((p) => p.id === id);

  const handleAdd = () => {
    if (picker) {
      onAdd(picker);
      setPicker('');
    }
  };

  return (
    <div className="space-y-2" data-testid="prod-bundle-items">
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No products in this bundle yet. Add the shirts + outerwear that ship together.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => {
            const p = byId(it.product_id);
            return (
              <div key={it.product_id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-3 py-2 rounded-lg bg-gray-50 border border-gray-100" data-testid={`prod-bundle-item-${it.product_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{p?.name || it.product_id}</p>
                  <p className="text-[11px] text-gray-400">{p?.product_kind || ''} {p?.base_price ? `· $${p.base_price.toFixed(2)} ea` : ''}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-500 font-medium">QTY</span>
                  <input type="number" min="1" step="1"
                    value={it.quantity}
                    onChange={(e) => onQty(it.product_id, e.target.value)}
                    className="w-16 px-2 py-1 rounded border border-gray-200 text-sm text-right"
                    data-testid={`prod-bundle-item-qty-${it.product_id}`} />
                </div>
                <button type="button" onClick={() => onRemove(it.product_id)}
                  className="p-1.5 rounded text-red-500 hover:bg-red-50"
                  data-testid={`prod-bundle-item-remove-${it.product_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <select value={picker} onChange={(e) => setPicker(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm"
          data-testid="prod-bundle-item-picker">
          <option value="">— Choose a product to add —</option>
          {candidates.map((p) => (
            <option key={p.id} value={p.id}>{p.name}{p.base_price ? ` — $${p.base_price.toFixed(2)}` : ''}</option>
          ))}
        </select>
        <button type="button" onClick={handleAdd} disabled={!picker}
          className="px-3 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] hover:bg-[#D88F1B] text-sm font-bold disabled:opacity-50"
          data-testid="prod-bundle-item-add">
          <Plus className="w-3.5 h-3.5 inline -mt-0.5" /> Add
        </button>
      </div>
      {schoolId && (
        <label className="flex items-center gap-2 text-[11px] text-gray-500 cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-[#F5A623]" data-testid="prod-bundle-show-all" />
          Show all products (not just this school&apos;s)
        </label>
      )}
    </div>
  );
};

const PlacementOverrideEditor = ({ library, value, onChange }) => {
  const overrideOn = value?.disabled_location_ids != null || value?.method_overrides != null;
  const disabledLocs = value?.disabled_location_ids || [];
  const methodOverrides = value?.method_overrides || {};

  const startOverride = () => {
    // Seed override with empty disabled list + per-location methods copied from library
    const seed = { ...(value || {}), disabled_location_ids: [], method_overrides: {} };
    (library.locations || []).forEach((loc) => {
      seed.method_overrides[loc.id] = [...(loc.methods || [])];
    });
    onChange(seed);
  };

  const clearOverride = () => {
    const { disabled_location_ids, method_overrides, ...pricingRules } = value || {};
    onChange(Object.keys(pricingRules).length ? pricingRules : null);
  };

  const toggleDisabled = (locId) => {
    const next = { ...(value || {}) };
    const set = new Set(disabledLocs);
    if (set.has(locId)) set.delete(locId); else set.add(locId);
    next.disabled_location_ids = Array.from(set);
    onChange(next);
  };

  const toggleMethod = (locId, method) => {
    const next = { ...(value || {}) };
    const current = new Set(next.method_overrides?.[locId] || []);
    if (current.has(method)) current.delete(method); else current.add(method);
    next.method_overrides = { ...(next.method_overrides || {}), [locId]: Array.from(current) };
    onChange(next);
  };

  // Helper: which methods are *allowed* in the library for this location (caps what we can toggle)
  const libMethods = (locId) => {
    const loc = (library.locations || []).find((x) => x.id === locId);
    return loc?.methods || [];
  };

  return (
    <div className="space-y-3" data-testid="prod-placement-override">
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
        <div className="text-xs text-gray-600">
          <strong className="text-[#1B3A5C]">{library.label}</strong> &nbsp;·&nbsp;
          {overrideOn ? (
            <span className="text-[#F5A623] font-medium">Per-product overrides active</span>
          ) : (
            <span>Inheriting library defaults</span>
          )}
          <Link to="/admin/placements" className="ml-2 text-[#F5A623] hover:underline">Manage library →</Link>
        </div>
        {overrideOn ? (
          <button type="button" onClick={clearOverride}
            className="px-3 py-1.5 rounded-full text-xs font-medium border border-gray-300 text-gray-700 hover:bg-white"
            data-testid="prod-clear-override">
            Use library defaults
          </button>
        ) : (
          <button type="button" onClick={startOverride}
            className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#F5A623] text-[#1B3A5C] hover:bg-[#D88F1B]"
            data-testid="prod-start-override">
            Override defaults
          </button>
        )}
      </div>

      <div className="space-y-2">
        {(library.locations || []).map((loc) => {
          const allowed = libMethods(loc.id);
          const isDisabled = disabledLocs.includes(loc.id);
          const activeMethods = overrideOn ? (methodOverrides[loc.id] || []) : (loc.methods || []);
          return (
            <div key={loc.id}
              className={`p-3 rounded-lg border ${isDisabled ? 'bg-red-50/30 border-red-100' : 'bg-white border-gray-200'}`}
              data-testid={`prod-loc-${loc.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className={`text-sm font-medium ${isDisabled ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{loc.label}</p>
                  <p className="text-[11px] text-gray-400">
                    {loc.size_range ? `${loc.size_range}" range` : ''}
                    {loc.default_size ? ` · default ${loc.default_size}"` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {allowed.map((m) => {
                    const on = activeMethods.includes(m);
                    return (
                      <button key={m} type="button"
                        disabled={!overrideOn}
                        onClick={() => toggleMethod(loc.id, m)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${on ? 'bg-[#F5A623] text-[#1B3A5C] border-[#F5A623]' : 'bg-white text-gray-500 border-gray-200'} ${!overrideOn ? 'opacity-60 cursor-not-allowed' : 'hover:border-[#F5A623]'}`}
                        data-testid={`prod-loc-${loc.id}-method-${m}`}>
                        {m}
                      </button>
                    );
                  })}
                  {overrideOn && (
                    <button type="button" onClick={() => toggleDisabled(loc.id)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${isDisabled ? 'bg-red-100 text-red-700 border-red-200' : 'bg-white text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-600'}`}
                      data-testid={`prod-loc-${loc.id}-toggle`}>
                      {isDisabled ? 'Re-enable' : 'Disable'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {(!library.locations || !library.locations.length) && (
          <p className="text-xs text-gray-400 italic">No placements in the library for this garment type.</p>
        )}
      </div>
    </div>
  );
};

const VariantsPanel = ({ variants, fits, onAdd, onUpdate, onSave, onDelete }) => {
  const availableFits = fits && fits.length ? fits : ['adult'];
  // Existing data uses capitalized keys ("Gender/Age", "Size", "Color"); new rows use lowercase.
  // These helpers read either casing and write a single canonical key going forward.
  const getFit = (opts) => opts?.fit || opts?.Fit || opts?.['Gender/Age'] || '';
  const getSize = (opts) => opts?.size || opts?.Size || '';
  const getColor = (opts) => opts?.color || opts?.Color || '';
  return (
    <div className="space-y-4" data-testid="prod-variants-panel">
      <div className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">Variants</h2>
            <p className="text-xs text-gray-500 mt-1">
              Fine-grained control over each fit × size × color combination — stock and price adjustment. Only use this if you need to track inventory per combo. The main product&apos;s base price + stock still apply when no variants exist.
            </p>
          </div>
          <button type="button" onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5A623] text-[#1B3A5C] hover:bg-[#D88F1B] text-xs font-bold"
            data-testid="prod-variant-add">
            <Plus className="w-3.5 h-3.5" /> Add variant
          </button>
        </div>

        {variants.length === 0 ? (
          <div className="p-8 text-center border-2 border-dashed border-gray-200 rounded-xl">
            <p className="text-sm text-gray-500">No variants yet.</p>
            <p className="text-xs text-gray-400 mt-1">Most products don&apos;t need this — keep it empty if your base price + stock are enough.</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                  <th className="py-2 px-2 font-bold">Fit</th>
                  <th className="py-2 px-2 font-bold">Size</th>
                  <th className="py-2 px-2 font-bold">Color</th>
                  <th className="py-2 px-2 font-bold">SKU</th>
                  <th className="py-2 px-2 font-bold text-right">Price adj.</th>
                  <th className="py-2 px-2 font-bold text-right">Stock</th>
                  <th className="py-2 px-2 font-bold">Active</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => (
                  <tr key={v.id} className="border-b border-gray-50" data-testid={`prod-variant-row-${v.id}`}>
                    <td className="py-1.5 px-2">
                      <select value={getFit(v.options) || 'adult'}
                        onChange={(e) => onUpdate(v.id, { options: { fit: e.target.value } })}
                        className={tableInput}>
                        {availableFits.map((f) => (<option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>))}
                        {/* Fallback: include the current value if it's an unrecognized legacy value */}
                        {getFit(v.options) && !availableFits.includes(getFit(v.options)) && (
                          <option value={getFit(v.options)}>{getFit(v.options)}</option>
                        )}
                      </select>
                    </td>
                    <td className="py-1.5 px-2">
                      <input value={getSize(v.options)}
                        onChange={(e) => onUpdate(v.id, { options: { size: e.target.value } })}
                        placeholder="M" className={`${tableInput} w-16`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <input value={getColor(v.options)}
                        onChange={(e) => onUpdate(v.id, { options: { color: e.target.value } })}
                        placeholder="Navy" className={`${tableInput} w-24`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <input value={v.sku || ''}
                        onChange={(e) => onUpdate(v.id, { sku: e.target.value })}
                        placeholder="—" className={`${tableInput} w-28`} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <input type="number" min="-1000" step="0.01" value={v.price_adjustment || 0}
                        onChange={(e) => onUpdate(v.id, { price_adjustment: parseFloat(e.target.value) || 0 })}
                        className={`${tableInput} w-20 text-right`} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <input type="number" min="0" step="1" value={v.stock_quantity || 0}
                        onChange={(e) => onUpdate(v.id, { stock_quantity: parseInt(e.target.value, 10) || 0 })}
                        className={`${tableInput} w-20 text-right`} />
                    </td>
                    <td className="py-1.5 px-2">
                      <input type="checkbox" checked={!!v.is_active}
                        onChange={(e) => onUpdate(v.id, { is_active: e.target.checked })}
                        className="accent-[#F5A623]" />
                    </td>
                    <td className="py-1.5 px-2 text-right whitespace-nowrap">
                      <button type="button" onClick={() => onSave(v)}
                        className="px-2 py-1 rounded text-[10px] font-bold bg-[#1B3A5C] text-white hover:bg-[#15293f] mr-1"
                        data-testid={`prod-variant-save-${v.id}`}>Save</button>
                      <button type="button" onClick={() => onDelete(v.id)}
                        className="p-1 rounded text-red-500 hover:bg-red-50"
                        data-testid={`prod-variant-delete-${v.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const tableInput = "w-full px-2 py-1 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#F5A623] text-xs";

const ImageLibraryModal = ({ headers, alreadyUsed, onPick, onClose }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/uploads/admin/files`, { headers });
        if (cancelled) return;
        // Images only, newest first.
        const imgs = (r.data || [])
          .filter((f) => (f.content_type || '').startsWith('image/'))
          .sort((a, b) => (b.updated || '').localeCompare(a.updated || ''));
        setFiles(imgs);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e?.message || 'Failed to load library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [headers]);

  const q = search.trim().toLowerCase();
  const visible = q
    ? files.filter((f) => (f.object_key || '').toLowerCase().includes(q))
    : files;

  const stop = (e) => e.stopPropagation();

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="image-library-modal">
      <div onClick={stop}
        className="w-full max-w-5xl max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-[#1B3A5C]">Image Library</h2>
            <p className="text-xs text-gray-500 mt-0.5">Pick any image you&apos;ve uploaded before. Click a tile to attach it to this product.</p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            data-testid="image-library-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by filename..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
              data-testid="image-library-search" />
          </div>
          {!loading && !err && (
            <p className="mt-2 text-xs text-gray-400">
              {visible.length} of {files.length} {files.length === 1 ? 'image' : 'images'}
              {q ? ' matched' : ' in library'}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading library...
            </div>
          ) : err ? (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{err}</span>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20 text-gray-400 text-sm">
              {files.length === 0
                ? 'No images uploaded yet. Use the Upload button to add some.'
                : `No images match "${search}".`}
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {visible.map((f) => {
                const used = alreadyUsed?.has(f.public_url);
                const filename = (f.object_key || '').split('/').pop() || f.object_key;
                return (
                  <button type="button" key={f.object_key}
                    onClick={() => onPick(f.public_url)}
                    disabled={used}
                    className={`group relative aspect-square rounded-xl overflow-hidden border bg-gray-50 transition-all ${used ? 'border-[#F5A623] cursor-not-allowed' : 'border-gray-200 hover:border-[#F5A623] hover:shadow-md cursor-pointer'}`}
                    data-testid={`image-library-item-${f.object_key}`}
                    title={filename}>
                    <img src={f.public_url} alt={filename}
                      className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-[10px] text-white truncate font-medium">{filename}</p>
                    </div>
                    {used && (
                      <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-[#F5A623] text-[#1B3A5C] text-[10px] font-bold">
                        IN USE
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminProductForm;
